import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonV3Component, SnackbarService } from '@gea/digital-ui-lib';
import { UserManagementService } from '../../../../services/user-management.service';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserGrantRow } from '../../user-management.model';

interface DeleteGrantsDialogData {
  userId: string;
  grants: UserGrantRow[];
  grantsCount: number;
}

@Component({
  selector: 'app-delete-grants-dialog',
  standalone: true,
  imports: [ButtonV3Component],
  templateUrl: './delete-grants-dialog.component.html',
  styleUrl: './delete-grants-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteGrantsDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig<DeleteGrantsDialogData>);
  private readonly userManagementService = inject(UserManagementService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);

  userId = this.config.data?.userId ?? '';
  grants: UserGrantRow[] = this.config.data?.grants ?? [];
  grantsCount = this.config.data?.grantCount ?? 0;
  grantsMap = this.grants?.reduce(
    (acc: any, row: UserGrantRow): any => {
      if (!acc[row.grant]) {
        acc[row.grant] = [];
      }
      acc[row.grant].push(row.targetId);
      return acc;
    },
    {} as Record<string, string[]>
  );

  deletingGrants = signal(false);

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    if (this.deletingGrants()) return;

    this.deletingGrants.set(true);
    this.userManagementService
      .deleteGrants(this.userId, this.grantsMap)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.deletingGrants.set(false))
      )
      .subscribe({
        next: () => {
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Grant(s) have been successfully removed from the user.',
          });
          this.dialogRef.close(true);
        },
      });
  }
}
